import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  UserCheck,
  Loader2,
  Mail,
  Phone,
  Building2,
  Check,
  X,
  Inbox,
  Eye,
  Cake,
  Briefcase,
  AlertCircle,
  Camera,
} from "lucide-react";
import { SearchableSelect } from "@/components/ui/searchable-select";

export const Route = createFileRoute("/_authenticated/approvals")({
  component: ApprovalsPage,
});

interface PendingDivision {
  id: string;
  name: string;
  isPrimary: boolean;
}

interface PendingUser {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  createdAt: string;
  divisions: PendingDivision[];
  birthDate: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  emergencyContactRelationship: string | null;
  profilePhotoFileId: string | null;
  jobTitleId: string | null;
  jobTitleName: string | null;
}

interface JobTitle {
  id: string;
  name: string;
}

interface OrgDivision {
  id: string;
  name: string;
}

const ROLE_OPTIONS = [
  { value: "field_personnel", label: "Field Personnel" },
  { value: "office_personnel", label: "Office Personnel" },
  { value: "div_admin", label: "Division Admin", adminOnly: true },
  { value: "subcontractor", label: "Subcontractor" },
] as const;

function formatDate(iso: string | null | undefined) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function ApprovalsPage() {
  const { user } = Route.useRouteContext();
  const queryClient = useQueryClient();

  const isAdmin = user.role === "admin";
  // Approving signups is an admin duty: super admins and division admins only.
  // Division supervisors (who may hold any base role) cannot approve.
  const canApprove = isAdmin || user.role === "div_admin";
  const roleOptions = ROLE_OPTIONS.filter((o) => isAdmin || !("adminOnly" in o && o.adminOnly));

  const { data, isLoading } = useQuery({
    queryKey: ["approvals", "pending"],
    queryFn: () => apiGet<{ data: PendingUser[]; canApprove: boolean }>("/api/approvals/pending"),
    enabled: canApprove,
    refetchInterval: 60_000,
  });

  const { data: jobTitlesData } = useQuery({
    queryKey: ["job-titles-list"],
    queryFn: () => apiGet<{ data: JobTitle[] }>("/api/admin/job-titles"),
    enabled: canApprove,
  });

  const { data: divisionsData } = useQuery({
    queryKey: ["divisions-list"],
    queryFn: () => apiGet<{ data: OrgDivision[] }>("/api/divisions"),
    enabled: canApprove,
  });

  const jobTitles = jobTitlesData?.data ?? [];
  const allDivisions = divisionsData?.data ?? [];

  const pending = data?.data ?? [];

  const [roles, setRoles] = useState<Record<string, string>>({});
  const [viewUser, setViewUser] = useState<PendingUser | null>(null);
  const [editRole, setEditRole] = useState("field_personnel");
  const [editDivisionIds, setEditDivisionIds] = useState<string[]>([]);
  const [editJobTitleId, setEditJobTitleId] = useState("");
  const [approvalNote, setApprovalNote] = useState("");
  const [photoLightboxOpen, setPhotoLightboxOpen] = useState(false);

  function openDetail(p: PendingUser) {
    setViewUser(p);
    setEditRole(roles[p.id] ?? "field_personnel");
    setEditDivisionIds(p.divisions.map((d) => d.id));
    setEditJobTitleId(p.jobTitleId ?? "");
    setApprovalNote("");
  }

  const { data: photoUrlData } = useQuery({
    queryKey: ["file-url", viewUser?.profilePhotoFileId],
    queryFn: () => apiGet<{ url: string }>(`/api/files/${viewUser!.profilePhotoFileId}/url`),
    enabled: !!viewUser?.profilePhotoFileId,
    staleTime: 10 * 60_000,
  });

  const approveMutation = useMutation({
    mutationFn: async ({ id, role, divisionIds, jobTitleId, note }: { id: string; role: string; divisionIds?: string[]; jobTitleId?: string | null; note?: string }) => {
      await apiPost(`/api/approvals/${id}/approve`, { role, divisionIds, jobTitleId, note: note?.trim() || undefined });
    },
    onSuccess: () => {
      toast.success("Worker approved");
      setViewUser(null);
      queryClient.invalidateQueries({ queryKey: ["approvals", "pending"] });
      queryClient.invalidateQueries({ queryKey: ["admin-notes-sent"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const rejectMutation = useMutation({
    mutationFn: (id: string) => apiPost(`/api/approvals/${id}/reject`),
    onSuccess: () => {
      toast.success("Registration rejected");
      setViewUser(null);
      queryClient.invalidateQueries({ queryKey: ["approvals", "pending"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (!canApprove) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center space-y-3">
        <UserCheck className="h-8 w-8 text-muted-foreground/40 mx-auto" />
        <p className="text-sm text-muted-foreground">
          You don&rsquo;t have access to worker approvals. Approving new signups is
          an admin duty (super admins and division admins).
        </p>
        <Link to="/" className="text-sm text-paris-red-text">
          Back to Home
        </Link>
      </div>
    );
  }

  const sheetBusy =
    (approveMutation.isPending && approveMutation.variables?.id === viewUser?.id) ||
    (rejectMutation.isPending && rejectMutation.variables === viewUser?.id);

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      <div className="flex items-center gap-2 pt-2">
        <UserCheck className="h-5 w-5 text-paris-red-text" />
        <h1 className="text-2xl font-bold text-text-primary">Worker Approvals</h1>
      </div>
      <p className="text-sm text-muted-foreground">
        New workers who registered for a division you supervise. Approve to let them
        sign in, or reject to deny access.
      </p>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-28 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      ) : pending.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Inbox className="h-8 w-8 text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">No pending approvals right now.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {pending.map((p) => {
            const role = roles[p.id] ?? "field_personnel";
            const busy =
              (approveMutation.isPending && approveMutation.variables?.id === p.id) ||
              (rejectMutation.isPending && rejectMutation.variables === p.id);
            return (
              <Card key={p.id}>
                <CardContent className="p-4 space-y-3">
                  <div>
                    <p className="font-semibold text-text-primary">{p.name}</p>
                    <div className="mt-1 space-y-1">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Mail className="h-3.5 w-3.5" />
                        <span>{p.email}</span>
                      </div>
                      {p.phone && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Phone className="h-3.5 w-3.5" />
                          <span>{p.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {p.divisions.length > 0 && (
                    <div className="flex items-start gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div className="flex flex-wrap gap-1.5">
                        {p.divisions.map((d) => (
                          <span
                            key={d.id}
                            className={cn(
                              "px-2 py-0.5 rounded-full text-xs border",
                              d.isPrimary
                                ? "bg-paris-red text-white border-paris-red"
                                : "bg-muted text-muted-foreground border-border"
                            )}
                          >
                            {d.isPrimary && "★ "}
                            {d.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <label className="text-xs text-muted-foreground">Role</label>
                    <select
                      value={role}
                      onChange={(e) => setRoles((r) => ({ ...r, [p.id]: e.target.value }))}
                      disabled={busy}
                      className="h-9 rounded-md border border-border bg-surface-base px-2 text-sm"
                    >
                      {roleOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <div className="flex gap-2 ml-auto">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDetail(p)}
                        disabled={busy}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => rejectMutation.mutate(p.id)}
                        disabled={busy}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => approveMutation.mutate({ id: p.id, role })}
                        disabled={busy}
                      >
                        {busy ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-1" />
                            Approve
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Worker detail sheet */}
      <Sheet open={!!viewUser} onOpenChange={(open) => { if (!open) { setViewUser(null); setPhotoLightboxOpen(false); } }}>
        <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Worker Details</SheetTitle>
          </SheetHeader>
          {viewUser && (
            <div className="space-y-5 mt-4 pb-6">
              {/* Photo + name */}
              <div className="flex items-center gap-4">
                {photoUrlData?.url ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setPhotoLightboxOpen(true)}
                      className="cursor-zoom-in rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <img
                        src={photoUrlData.url}
                        alt={viewUser.name}
                        className="h-16 w-16 rounded-full object-cover border border-border"
                      />
                    </button>
                    {photoLightboxOpen && createPortal(
                      <div
                        className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center"
                        onClick={() => setPhotoLightboxOpen(false)}
                      >
                        <img
                          src={photoUrlData.url}
                          alt={viewUser.name}
                          className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
                        />
                      </div>,
                      document.body
                    )}
                  </>
                ) : (
                  <div className="h-16 w-16 rounded-full bg-surface-hover flex items-center justify-center border border-border">
                    <Camera className="h-6 w-6 text-muted-foreground/40" />
                  </div>
                )}
                <div>
                  <p className="font-semibold text-text-primary text-lg">{viewUser.name}</p>
                  <p className="text-sm text-muted-foreground">{viewUser.email}</p>
                </div>
              </div>

              {/* Contact info */}
              <div className="space-y-1.5 text-sm">
                {viewUser.phone && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-3.5 w-3.5 shrink-0" />
                    <span>{viewUser.phone}</span>
                  </div>
                )}
                {viewUser.birthDate && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Cake className="h-3.5 w-3.5 shrink-0" />
                    <span>DOB: {formatDate(viewUser.birthDate)}</span>
                  </div>
                )}
              </div>

              {/* Emergency contact */}
              {viewUser.emergencyContactName && (
                <div className="rounded-md border border-border bg-surface-base/50 px-3 py-2.5 space-y-0.5">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1">
                    Emergency Contact
                  </p>
                  <div className="flex items-start gap-2 text-sm">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-muted-foreground" />
                    <div>
                      <span className="font-medium">{viewUser.emergencyContactName}</span>
                      {viewUser.emergencyContactRelationship && (
                        <span className="text-muted-foreground ml-1">
                          ({viewUser.emergencyContactRelationship})
                        </span>
                      )}
                      {viewUser.emergencyContactPhone && (
                        <p className="text-muted-foreground">{viewUser.emergencyContactPhone}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Divider */}
              <div className="border-t border-border pt-4 space-y-3">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Assign before approving
                </p>

                {/* Role */}
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground">Role</label>
                  <select
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value)}
                    disabled={sheetBusy}
                    className="w-full h-9 rounded-md border border-border bg-surface-base px-2 text-sm"
                  >
                    {roleOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Job title */}
                {jobTitles.length > 0 && (
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">
                      <Briefcase className="h-3 w-3 inline mr-1" />
                      Job Title
                    </label>
                    <SearchableSelect
                      value={editJobTitleId}
                      onChange={setEditJobTitleId}
                      disabled={sheetBusy}
                      allLabel={`— Keep as registered (${viewUser.jobTitleName ?? "none"}) —`}
                      allValue=""
                      options={jobTitles.map((jt) => ({ value: jt.id, label: jt.name }))}
                    />
                  </div>
                )}

                {/* Divisions */}
                {allDivisions.length > 0 && (
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">
                      <Building2 className="h-3 w-3 inline mr-1" />
                      Divisions <span className="opacity-60">(tap to toggle; first = primary)</span>
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {allDivisions.map((d) => {
                        const isSelected = editDivisionIds.includes(d.id);
                        const isPrimary = editDivisionIds[0] === d.id;
                        return (
                          <button
                            key={d.id}
                            type="button"
                            disabled={sheetBusy}
                            onClick={() =>
                              setEditDivisionIds((prev) =>
                                prev.includes(d.id)
                                  ? prev.filter((x) => x !== d.id)
                                  : [...prev, d.id]
                              )
                            }
                            className={cn(
                              "px-2.5 py-1 rounded-full text-xs border transition-colors",
                              isSelected
                                ? isPrimary
                                  ? "bg-paris-red text-white border-paris-red"
                                  : "bg-paris-red/15 text-paris-red-text border-paris-red/40"
                                : "bg-card text-muted-foreground border-border hover:border-muted-foreground"
                            )}
                          >
                            {isPrimary && "★ "}
                            {d.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Note for worker */}
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">
                  Note for worker <span className="opacity-60">(optional — shown on their dashboard after approval)</span>
                </label>
                <textarea
                  value={approvalNote}
                  onChange={(e) => setApprovalNote(e.target.value)}
                  disabled={sheetBusy}
                  placeholder="e.g. Welcome to the team! Please complete your safety orientation by Friday."
                  rows={3}
                  className="w-full rounded-md border border-border bg-surface-base px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground/50"
                />
              </div>

              {/* Action buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => rejectMutation.mutate(viewUser.id)}
                  disabled={sheetBusy}
                >
                  {rejectMutation.isPending && rejectMutation.variables === viewUser.id ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <X className="h-4 w-4 mr-1" />
                  )}
                  Reject
                </Button>
                <Button
                  className="flex-1"
                  onClick={() =>
                    approveMutation.mutate({
                      id: viewUser.id,
                      role: editRole,
                      divisionIds: editDivisionIds.length > 0 ? editDivisionIds : undefined,
                      jobTitleId: editJobTitleId || viewUser.jobTitleId || null,
                      note: approvalNote,
                    })
                  }
                  disabled={sheetBusy}
                >
                  {approveMutation.isPending && approveMutation.variables?.id === viewUser.id ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <Check className="h-4 w-4 mr-1" />
                  )}
                  Approve
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
